import axios, { AxiosError } from 'axios';

/**
 * Project Service
 *
 * Handles all project-related API requests to the backend.
 */

// API Base URL from environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// Project endpoints
const PROJECT_ENDPOINTS = {
  list: `${API_BASE_URL}/projects`,
  byId: (id: string) => `${API_BASE_URL}/projects/${id}`,
  create: `${API_BASE_URL}/projects`,
  update: (id: string) => `${API_BASE_URL}/projects/${id}`,
  delete: (id: string) => `${API_BASE_URL}/projects/${id}`,
  members: (id: string) => `${API_BASE_URL}/projects/${id}/members`,
  addMember: (id: string) => `${API_BASE_URL}/projects/${id}/members`,
  removeMember: (id: string, userId: string) => `${API_BASE_URL}/projects/${id}/members/${userId}`,
  updateStakeholder: (id: string, memberId: string) => `${API_BASE_URL}/projects/${id}/stakeholders/${memberId}`,
  removeStakeholder: (id: string, memberId: string) => `${API_BASE_URL}/projects/${id}/stakeholders/${memberId}`,
  techStack: (id: string) => `${API_BASE_URL}/projects/${id}/tech-stack`,
  addTechStack: (id: string) => `${API_BASE_URL}/projects/${id}/tech-stack`,
  removeTechStack: (id: string, techStackItemId: string) => `${API_BASE_URL}/projects/${id}/tech-stack/${techStackItemId}`,
  generateCode: `${API_BASE_URL}/projects/generate-code`,
  generateExecutiveSummary: (id: string) => `${API_BASE_URL}/projects/${id}/executive-summary/generate`,
  saveManualExecutiveSummary: (id: string) => `${API_BASE_URL}/projects/${id}/executive-summary/manual`,
  generateRequirementsReview: (id: string) => `${API_BASE_URL}/projects/${id}/requirements-review`,
  brd: (id: string) => `${API_BASE_URL}/projects/${id}/brd`
};

/**
 * Project interface matching backend
 */
export interface Project {
  id: string;
  company_id: string;
  name: string;
  project_code: string;
  description?: string | null;
  in_scope?: string | null;
  out_scope?: string | null;
  owner_id: string;
  project_type_id?: string | null;
  status: 'draft' | 'ready' | 'in_progress' | 'done';
  start_date?: string | null;
  target_completion_date?: string | null;
  actual_completion_date?: string | null;
  tags?: string | null;
  created_at: string;
  updated_at?: string | null;
  is_active: boolean;
  // Executive Summary fields (AI Integration)
  executive_summary?: string | null;
  executive_summary_generated_at?: string | null;
  executive_summary_generated_by?: string | null;
  executive_summary_generated_by_name?: string | null; // User's full name from JOIN
  executive_summary_method?: 'ai' | 'manual' | null;
  executive_summary_tokens_used?: number | null;
  executive_summary_model?: string | null;
  // BRD fields (AI Integration)
  brd_content?: string | null;
  brd_generated_at?: string | null;
  brd_generated_by?: string | null;
  brd_generated_by_name?: string | null;
  brd_method?: 'ai' | 'manual' | null;
  brd_tokens_used?: number | null;
  brd_model?: string | null;
  // Requirements Insights tracking
  requirements_insights_generation_count?: number | null;
}

/**
 * Project with extended information
 */
export interface ProjectWithDetails extends Project {
  member_count: number;
  requirement_count: number;
  approved_count: number;
  rejected_count: number;
  owner_name: string;
}

/**
 * Project member interface
 */
export interface ProjectMember {
  id: string;
  project_id: string;
  user_id: string | null;
  is_owner: boolean;
  role?: 'project_manager' | 'contributor' | 'stakeholder' | null;
  stakeholder_name?: string | null;
  stakeholder_responsibility?: string | null;
  added_by?: string | null;
  added_at: string;
  is_active: boolean;
  // Extended user details
  user_email?: string;
  user_first_name?: string;
  user_last_name?: string;
  user_role?: string;
}

/**
 * Project creation input
 */
export interface CreateProjectInput {
  name: string;
  project_code: string;
  description?: string;
  in_scope?: string;
  out_scope?: string;
  owner_id?: string;
  project_type_id?: string;
  status?: 'draft' | 'ready' | 'in_progress' | 'done';
  start_date?: string;
  end_date?: string;
  target_completion_date?: string;
  tags?: string;
}

/**
 * Project update input
 */
export interface UpdateProjectInput {
  name?: string;
  project_code?: string;
  description?: string;
  in_scope?: string;
  out_scope?: string;
  project_type_id?: string | null;
  status?: 'draft' | 'ready' | 'in_progress' | 'done';
  start_date?: string;
  target_completion_date?: string;
  actual_completion_date?: string;
  tags?: string;
}

/**
 * Add member input
 */
export interface AddMemberInput {
  user_id?: string;
  role?: 'project_manager' | 'contributor' | 'stakeholder';
  stakeholder_name?: string;
  stakeholder_responsibility?: string;
}

/**
 * Add stakeholder input (freeform name entry)
 */
export interface AddStakeholderInput {
  stakeholder_name: string;
  stakeholder_responsibility?: string;
  role: 'stakeholder';
}

/**
 * List projects query parameters
 */
export interface ListProjectsParams {
  status?: string;
  owner_id?: string;
  search?: string;
}

/**
 * API Response interfaces
 */
export interface ProjectsListResponse {
  success: boolean;
  data: {
    projects: ProjectWithDetails[];
    total: number;
    unique_member_count?: number;
  };
}

export interface ProjectResponse {
  success: boolean;
  data: {
    project: Project;
    members?: ProjectMember[];
  };
}

export interface ProjectMembersResponse {
  success: boolean;
  data: {
    members: ProjectMember[];
  };
}

/**
 * Project tech stack item with details
 */
export interface ProjectTechStackItem {
  id: string;
  project_id: string;
  tech_stack_item_id: string;
  added_by: string;
  added_at: string;
  notes?: string | null;
  is_active: boolean;
  // Extended tech stack details
  tech_name: string;
  tech_category: string;
  tech_subcategory?: string | null;
  tech_description?: string | null;
  tech_icon_url?: string | null;
  tech_is_popular: boolean;
}

/**
 * Add tech stack input
 */
export interface AddTechStackInput {
  tech_stack_item_id: string;
  notes?: string;
}

export interface ProjectTechStackResponse {
  success: boolean;
  data: {
    techStack: ProjectTechStackItem[];
  };
}

export interface MessageResponse {
  success: boolean;
  message: string;
}

export interface APIError {
  error: string;
  details?: string;
}

/**
 * Executive Summary Response interfaces
 */
export interface ExecutiveSummaryData {
  executive_summary: string;
  generated_at: string;
  tokens_used?: number;
  model?: string;
  method: 'ai' | 'manual';
}

export interface ExecutiveSummaryResponse {
  success: boolean;
  data: ExecutiveSummaryData;
}

/**
 * Manual summary input
 */
export interface SaveManualSummaryInput {
  executive_summary: string;
}

/**
 * Handle API errors
 */
function handleAPIError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<APIError>;
    const errorMessage = axiosError.response?.data?.error || axiosError.message;
    throw new Error(errorMessage);
  }
  throw error;
}

/**
 * Create a new project
 */
export async function createProject(
  data: CreateProjectInput,
  token: string
): Promise<Project> {
  try {
    const response = await axios.post<{success: boolean; data: Project}>(
      PROJECT_ENDPOINTS.create,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Create a new project with initial requirements in a single transaction
 */
export interface CreateProjectWithRequirementsInput {
  project: CreateProjectInput;
  requirements: Array<{
    title: string;
    description: string;
    requirement_type: 'functional' | 'non_functional' | 'business' | 'technical';
    priority: 'critical' | 'high' | 'medium' | 'low';
    category: string;
  }>;
}

export interface CreateProjectWithRequirementsResponse {
  project: Project;
  requirements: any[];
  requirementsCount: number;
}

export async function createProjectWithRequirements(
  data: CreateProjectWithRequirementsInput,
  token: string
): Promise<CreateProjectWithRequirementsResponse> {
  try {
    const response = await axios.post<{success: boolean; data: CreateProjectWithRequirementsResponse}>(
      `${API_BASE_URL}/projects/with-requirements`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * List all projects
 */
export async function listProjects(
  params: ListProjectsParams,
  token: string
): Promise<{ projects: ProjectWithDetails[]; total: number; unique_member_count?: number }> {
  try {
    const response = await axios.get<ProjectsListResponse>(PROJECT_ENDPOINTS.list, {
      params,
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get project by ID
 */
export async function getProjectById(id: string, token: string): Promise<{project: Project; members?: ProjectMember[]}> {
  try {
    const response = await axios.get<ProjectResponse>(PROJECT_ENDPOINTS.byId(id), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Update project
 */
export async function updateProject(
  id: string,
  data: UpdateProjectInput,
  token: string
): Promise<Project> {
  try {
    const response = await axios.put<{success: boolean; data: Project}>(
      PROJECT_ENDPOINTS.update(id),
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Delete project
 */
export async function deleteProject(id: string, token: string): Promise<void> {
  try {
    await axios.delete<MessageResponse>(PROJECT_ENDPOINTS.delete(id), {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Add member to project
 */
export async function addProjectMember(
  projectId: string,
  data: AddMemberInput,
  token: string
): Promise<ProjectMember> {
  try {
    const response = await axios.post<{success: boolean; data: ProjectMember}>(
      PROJECT_ENDPOINTS.addMember(projectId),
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Remove member from project
 */
export async function removeProjectMember(
  projectId: string,
  userId: string,
  token: string
): Promise<void> {
  try {
    await axios.delete<MessageResponse>(
      PROJECT_ENDPOINTS.removeMember(projectId, userId),
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Update stakeholder
 */
export async function updateStakeholder(
  projectId: string,
  memberId: string,
  data: { stakeholder_name: string; stakeholder_responsibility?: string },
  token: string
): Promise<void> {
  try {
    await axios.put<MessageResponse>(
      PROJECT_ENDPOINTS.updateStakeholder(projectId, memberId),
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Remove stakeholder from project
 */
export async function removeStakeholder(
  projectId: string,
  memberId: string,
  token: string
): Promise<void> {
  try {
    await axios.delete<MessageResponse>(
      PROJECT_ENDPOINTS.removeStakeholder(projectId, memberId),
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get project members
 */
export async function getProjectMembers(projectId: string, token: string): Promise<ProjectMember[]> {
  try {
    const response = await axios.get<ProjectMembersResponse>(
      PROJECT_ENDPOINTS.members(projectId),
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data.members;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get project tech stack
 */
export async function getProjectTechStack(projectId: string, token: string): Promise<ProjectTechStackItem[]> {
  try {
    const response = await axios.get<ProjectTechStackResponse>(
      PROJECT_ENDPOINTS.techStack(projectId),
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data.techStack;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Add tech stack item to project
 */
export async function addProjectTechStack(
  projectId: string,
  data: AddTechStackInput,
  token: string
): Promise<ProjectTechStackItem> {
  try {
    const response = await axios.post<{success: boolean; data: ProjectTechStackItem}>(
      PROJECT_ENDPOINTS.addTechStack(projectId),
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Remove tech stack item from project
 */
export async function removeProjectTechStack(
  projectId: string,
  techStackItemId: string,
  token: string
): Promise<void> {
  try {
    await axios.delete<MessageResponse>(
      PROJECT_ENDPOINTS.removeTechStack(projectId, techStackItemId),
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Generate the next available project code for the user's company
 */
export async function generateProjectCode(token: string, projectName: string): Promise<string> {
  try {
    const response = await axios.get(PROJECT_ENDPOINTS.generateCode, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      params: {
        projectName
      }
    });
    return response.data.data.project_code;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Generate AI-powered executive summary for a project
 */
export async function generateExecutiveSummary(
  projectId: string,
  token: string
): Promise<ExecutiveSummaryData> {
  try {
    const response = await axios.post<ExecutiveSummaryResponse>(
      PROJECT_ENDPOINTS.generateExecutiveSummary(projectId),
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Save manually written executive summary for a project
 */
export async function saveManualExecutiveSummary(
  projectId: string,
  data: SaveManualSummaryInput,
  token: string
): Promise<ExecutiveSummaryData> {
  try {
    const response = await axios.post<ExecutiveSummaryResponse>(
      PROJECT_ENDPOINTS.saveManualExecutiveSummary(projectId),
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * BRD Response interfaces
 */
export interface BRDData {
  brd_content: string;
  generated_at: string;
  tokens_used?: number;
  model?: string;
  method: 'ai' | 'manual';
}

export interface BRDResponse {
  success: boolean;
  data: BRDData;
}

/**
 * Generate or save BRD input
 */
export interface GenerateOrSaveBRDInput {
  method: 'ai' | 'manual';
  content?: string; // Required for manual method
}

/**
 * Generate AI-powered BRD for a project
 */
export async function generateBRD(
  projectId: string,
  token: string
): Promise<BRDData> {
  try {
    const response = await axios.post<BRDResponse>(
      PROJECT_ENDPOINTS.brd(projectId),
      { method: 'ai' },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Save manually written BRD for a project
 */
export async function saveManualBRD(
  projectId: string,
  content: string,
  token: string
): Promise<BRDData> {
  try {
    const response = await axios.post<BRDResponse>(
      PROJECT_ENDPOINTS.brd(projectId),
      { method: 'manual', content },
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}

/**
 * Get BRD for a project
 */
export async function getBRD(
  projectId: string,
  token: string
): Promise<BRDData | null> {
  try {
    const response = await axios.get<BRDResponse>(
      PROJECT_ENDPOINTS.brd(projectId),
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data.data;
  } catch (error: any) {
    // Return null if no BRD found (404), but throw for other errors
    if (error.response?.status === 404) {
      return null;
    }
    handleAPIError(error);
  }
}

/**
 * Requirements Review Data Types
 */
export interface RequirementInsight {
  requirementId: string;
  requirementTitle: string;
  requirementType: string;
  requirementPriority: string;
  insights: string;
  tokensUsed: number;
  error?: string;
}

export interface RequirementsReviewData {
  projectId: string;
  projectName: string;
  requirementsProcessed: number;
  tokensUsed: number;
  model: string;
  results: RequirementInsight[];
  generationCount?: number;
  requirementsWithoutInsightsCount?: number;
  newRequirementsOnly?: boolean;
}

/**
 * Get existing requirements insights
 */
export async function getRequirementsInsights(projectId: string, token: string): Promise<RequirementsReviewData | null> {
  try {
    const response = await axios.get<{ success: boolean; data: RequirementsReviewData }>(
      PROJECT_ENDPOINTS.generateRequirementsReview(projectId),
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    return response.data.data;
  } catch (error: any) {
    // Return null if no insights found (404), but throw for other errors
    if (error.response?.status === 404) {
      return null;
    }
    handleAPIError(error);
    throw error;
  }
}

/**
 * Generate requirements review with AI insights
 */
export async function generateRequirementsReview(
  projectId: string,
  token: string,
  newRequirementsOnly: boolean = false
): Promise<RequirementsReviewData> {
  try {
    const response = await axios.post<{ success: boolean; data: RequirementsReviewData }>(
      PROJECT_ENDPOINTS.generateRequirementsReview(projectId),
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { newRequirementsOnly }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
    throw error; // Re-throw after logging
  }
}

export default {
  createProject,
  listProjects,
  getProjectById,
  updateProject,
  deleteProject,
  addProjectMember,
  removeProjectMember,
  getProjectMembers,
  getProjectTechStack,
  addProjectTechStack,
  removeProjectTechStack,
  generateProjectCode,
  generateExecutiveSummary,
  saveManualExecutiveSummary,
  generateBRD,
  saveManualBRD,
  getBRD,
  generateRequirementsReview,
  getProjectActivities
};

/**
 * Activity interface
 */
export interface Activity {
  id: string;
  company_id: string | null;
  project_id: string | null;
  requirement_id: string | null;
  user_id: string;
  action_type: string;
  description: string;
  created_at: string;
  user_first_name?: string;
  user_last_name?: string;
  user_email?: string;
}

/**
 * Get recent activities for a project
 */
export async function getProjectActivities(
  projectId: string,
  token: string,
  limit: number = 10
): Promise<Activity[]> {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/projects/${projectId}/activities`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: { limit }
      }
    );
    return response.data.data;
  } catch (error) {
    handleAPIError(error);
  }
}
